'use strict';

/**
 * Pippei cart: drawer + cart page controller and the «Forventet klar» cart guard.
 *
 * - Every cart change is one request to the Ajax cart API with `sections`, so the response already carries the
 *   re-rendered drawer (and the cart page section when we are on /cart). No second fetch.
 * - Add to cart from any form[data-product-form] opens the drawer instead of loading /cart.
 * - Mobile first: full-width panel, body scroll lock that works on iOS, Android back button closes the drawer,
 *   focus trap and Esc for keyboard and screen reader users.
 * - Quick add from product cards ([data-quick-add] = variant id) adds one item and opens the drawer.
 * - Engraving (blocks/product-engraving.liquid): the product and the chosen engravings are added in one
 *   all-or-nothing /cart/add.js call, linked by _gravering_id / _gravering_for (contract shared with the portal,
 *   ported from the live assets/pippei-gravering.js). Removing a product removes its engravings in the same request;
 *   the link guard removes engravings left without a product and strips _gravering_id from a product whose last
 *   engraving is gone.
 * - The guard keeps the line property «Forventet klar» equal to the delivery text rendered by
 *   snippets/line-delivery.liquid ([data-pippei-fk]); rules and limits are ported from the live Kalles guard
 *   (assets/pippei-forventet-klar.js, 24.09.2026). Since 12.10.2026 (order #11207) it also covers engraving lines,
 *   removes old Kalles names («_Forventet klar») and clears a passed date when no new date is known.
 *   «_Forhåndsbestilt vare» is left alone on purpose (Knut may use it in notification templates).
 */
(function () {
  if (customElements.get('cart-drawer')) return;

  var routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var URL_ADD = routes + 'cart/add.js';
  var URL_CHANGE = routes + 'cart/change.js';
  var URL_UPDATE = routes + 'cart/update.js';
  var onCartPage = /\/cart\/?$/.test(window.location.pathname);

  function sectionIds() {
    var ids = [];
    document.querySelectorAll('[data-cart-root][data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function jsonPost(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(readJson);
  }

  function readJson(r) {
    return r.json().then(function (j) {
      if (!r.ok || j.status) {
        var err = new Error(j.description || j.message || ('HTTP ' + r.status));
        err.status = r.status;
        throw err;
      }
      return j;
    });
  }

  function sectionParams(body) {
    body.sections = sectionIds().join(',');
    body.sections_url = window.location.pathname;
    return body;
  }

  /* Liquid renders `item.properties | json` as [name, value] pairs; both forms are accepted.
     null means unreadable: callers then leave the line alone, because change.js replaces the whole property set. */
  function parseProps(raw) {
    if (raw === null || raw === undefined || raw === '') return null;
    try {
      var p = JSON.parse(raw);
      if (Array.isArray(p)) {
        var o = {};
        for (var i = 0; i < p.length; i++) {
          if (!Array.isArray(p[i]) || p[i].length !== 2 || typeof p[i][0] !== 'string') return null;
          o[p[i][0]] = p[i][1] === null || p[i][1] === undefined ? '' : String(p[i][1]);
        }
        return o;
      }
      return (p && typeof p === 'object') ? p : null;
    } catch (e) { return null; }
  }

  function toast(message) {
    var el = document.createElement('p');
    el.setAttribute('role', 'alert');
    el.className = 'fixed inset-x-4 bottom-4 z-[70] mx-auto max-w-sm rounded bg-bark px-4 py-3 text-sm text-white shadow-lg';
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(function () { el.remove(); }, 5000);
  }

  /* ---------- rendering ---------- */

  function render(sections) {
    if (!sections) return;
    document.querySelectorAll('[data-cart-root][data-section-id]').forEach(function (root) {
      var html = sections[root.getAttribute('data-section-id')];
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-cart-root-content]');
      var current = root.querySelector('[data-cart-root-content]');
      if (!fresh || !current) return;
      var scroller = current.querySelector('[data-drawer-scroll]');
      var scrollTop = scroller ? scroller.scrollTop : 0;
      var focusKey = null, focusSel = null;
      var active = document.activeElement;
      if (active && current.contains(active)) {
        var line = active.closest('[data-cart-line]');
        focusKey = line && line.getAttribute('data-line-key');
        focusSel = active.hasAttribute('data-line-qty') ? '[data-line-qty]' : active.hasAttribute('data-qty-change') ? '[data-qty-change="' + active.getAttribute('data-qty-change') + '"]' : null;
      }
      current.replaceWith(fresh);
      var newScroller = fresh.querySelector('[data-drawer-scroll]');
      if (newScroller) newScroller.scrollTop = scrollTop;
      if (focusKey && focusSel) {
        var target = fresh.querySelector('[data-line-key="' + CSS.escape(focusKey) + '"] ' + focusSel);
        if (target) target.focus({ preventScroll: true });
      }
    });
    updateCount();
    enforceCaps();
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    if (!links.reconcile()) guard.schedule();   // link repairs first; their render schedules the guard
  }

  /* Long-term sold-out variants have a quantity limit (data-max, snippets/qty-cap.liquid): stock plus confirmed deliveries.
     Every render checks the lines, so an extra add from the product page or a quick add is cut back to the limit too.
     data-max="0" (nothing in stock or on the way, Knut 2026-10-14) removes the line: no overselling. */
  function lineMax(line) { var n = parseInt(line.getAttribute('data-max'), 10); return isNaN(n) ? null : n; }
  var capNoticed = false;
  function enforceCaps() {
    var seen = {};
    document.querySelectorAll('[data-cart-root] [data-cart-line][data-max]').forEach(function (line) {
      var key = line.getAttribute('data-line-key'), max = lineMax(line);
      var input = line.querySelector('[data-line-qty]');
      if (seen[key] || max === null || !input || (parseInt(input.value, 10) || 0) <= max) return;
      seen[key] = true;
      var cs = window.PippeiCartStrings || {};
      var msg = max === 0 ? (cs.long_sold_out || 'Varen er langtidsutsolgt og er tatt ut av handlekurven.') : (cs.max_qty || 'Maks __MAX__ stk. kan bestilles av dette produktet.').replace('__MAX__', max);
      if (!capNoticed) { capNoticed = true; toast(msg); setTimeout(function () { capNoticed = false; }, 6000); }
      changeLine(line, max);
    });
  }

  function updateCount() {
    var holder = document.querySelector('#CartDrawer [data-cart-count-value]');
    if (!holder) return;
    var count = parseInt(holder.getAttribute('data-cart-count-value'), 10) || 0;
    document.querySelectorAll('[data-cart-count]').forEach(function (b) {
      b.textContent = count;
      b.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach(function (a) {
      var tpl = a.getAttribute('data-label-template');
      if (tpl) a.setAttribute('aria-label', tpl.replace('__COUNT__', count));
    });
  }

  function setBusy(on) {
    document.querySelectorAll('[data-cart-root]').forEach(function (root) {
      root.toggleAttribute('aria-busy', on);
      root.classList.toggle('is-busy', on);
      root.querySelectorAll('[data-cart-checkout]').forEach(function (b) { b.disabled = on; });
    });
  }

  /* ---------- line changes ---------- */

  var changeQueue = Promise.resolve();
  function changeLine(lineEl, quantity) {
    var key = lineEl.getAttribute('data-line-key');
    var errorEl = lineEl.querySelector('[data-line-error]');
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
    /* Removing a product also removes its engravings, in the same request. */
    var url = URL_CHANGE, body = { id: key, quantity: quantity };
    var gid = lineEl.getAttribute('data-engraving-id');
    var root = lineEl.closest('[data-cart-root]');
    if (quantity === 0 && gid && root) {
      var updates = {};
      updates[key] = 0;
      root.querySelectorAll('[data-engraving-for="' + CSS.escape(gid) + '"]').forEach(function (el) { updates[el.getAttribute('data-line-key')] = 0; });
      url = URL_UPDATE; body = { updates: updates };
    }
    changeQueue = changeQueue.then(function () {
      setBusy(true);
      return jsonPost(url, sectionParams(body))
        .then(function (cart) { render(cart.sections); })
        .catch(function (e) {
          var el = document.querySelector('[data-line-key="' + CSS.escape(key) + '"] [data-line-error]') || errorEl;
          if (el) { el.textContent = e.message || window.PippeiCartStrings.error; el.hidden = false; }
        })
        .then(function () { setBusy(false); });
    });
    return changeQueue;
  }

  var qtyTimers = new WeakMap();
  function setMinusState(line, qty) {
    var minus = line.querySelector('[data-qty-change="-1"]');
    if (!minus) return;
    var remove = qty <= 1;
    minus.setAttribute('data-remove', remove ? 'true' : 'false');
    minus.setAttribute('aria-label', minus.getAttribute(remove ? 'data-label-remove' : 'data-label-decrease'));
  }
  document.addEventListener('click', function (ev) {
    var t = ev.target;
    var step = t.closest('[data-qty-change]');
    if (step && step.closest('[data-cart-root]')) {
      ev.preventDefault();
      var line = step.closest('[data-cart-line]');
      var input = line.querySelector('[data-line-qty]');
      var next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute('data-qty-change'), 10));
      if (lineMax(line)) next = Math.min(next, lineMax(line));
      input.value = next;
      setMinusState(line, next);
      clearTimeout(qtyTimers.get(line));
      // Trash tap (1 -> 0) removes at once; plain steps are debounced into one request.
      qtyTimers.set(line, setTimeout(function () { changeLine(line, next); }, next === 0 ? 0 : 350));
      return;
    }
    var remove = t.closest('[data-line-remove]');
    if (remove && remove.closest('[data-cart-root]')) {
      ev.preventDefault();
      changeLine(remove.closest('[data-cart-line]'), 0);
      return;
    }
    var quick = t.closest('[data-quick-add]');
    if (quick) {
      ev.preventDefault();
      quickAdd(quick);
      return;
    }
    var toggle = t.closest('[data-cart-drawer-toggle]');
    if (toggle && !onCartPage) {
      ev.preventDefault();
      drawer() && drawer().open(toggle);
      return;
    }
    if (t.closest('[data-drawer-close]')) {
      ev.preventDefault();
      drawer() && drawer().close();
    }
  });

  document.addEventListener('change', function (ev) {
    var input = ev.target.closest && ev.target.closest('[data-line-qty]');
    if (!input || !input.closest('[data-cart-root]')) return;
    var line = input.closest('[data-cart-line]');
    clearTimeout(qtyTimers.get(line));
    var wanted = Math.max(0, parseInt(input.value, 10) || 0);
    if (lineMax(line) && wanted > lineMax(line)) { wanted = lineMax(line); input.value = wanted; }
    changeLine(line, wanted);
  });

  /* Enter in a quantity field on the cart page should not submit the no-JS form. */
  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Enter' && ev.target.matches && ev.target.matches('[data-cart-root] [data-line-qty]')) {
      ev.preventDefault();
      ev.target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* ---------- add to cart ---------- */

  function quickAdd(btn) {
    if (btn.getAttribute('aria-busy') === 'true') return;
    if (!drawer()) { window.location.href = routes + 'cart/add?id=' + encodeURIComponent(btn.getAttribute('data-quick-add')) + '&quantity=1'; return; }
    btn.setAttribute('aria-busy', 'true');
    jsonPost(URL_ADD, sectionParams({ items: [{ id: parseInt(btn.getAttribute('data-quick-add'), 10), quantity: 1 }] }))
      .then(function (res) {
        render(res.sections);
        btn.setAttribute('data-state', 'added');
        setTimeout(function () { btn.removeAttribute('data-state'); }, 1500);
        drawer().open(btn);
      })
      .catch(function (e) { toast(e.message || window.PippeiCartStrings.error); })
      .then(function () { btn.removeAttribute('aria-busy'); });
  }

  /* Several items in one /cart/add.js call (all or nothing), then the drawer opens; used by «Min ønskeliste» (assets/wishlist.js).
     items: [{ id, quantity, properties? }] are sent unchanged. Ønskeliste port 4: a giver's line on a gift list can carry the hidden
     property _pippei_onskeliste (the order link); the delivery-date guard below keeps it, since it copies all properties from data-props
     (snippets/line-delivery.liquid). Resolves with the cart answer; an error shows the toast and rejects. */
  function addItems(items, opener) {
    if (!drawer()) {
      return jsonPost(URL_ADD, { items: items }).then(function (res) { window.location.href = routes + 'cart'; return res; },
        function (e) { toast(e.message || (window.PippeiCartStrings && window.PippeiCartStrings.error) || 'Error'); throw e; });
    }
    var before = cartCount();
    return jsonPost(URL_ADD, sectionParams({ items: items }))
      .then(function (res) { render(res.sections); if (!onCartPage) drawer().open(opener); return res; },
        function (e) {
          toast(e.message || (window.PippeiCartStrings && window.PippeiCartStrings.error) || 'Error');
          /* A 422 for too many can still have added what was in stock (Knut 2026-10-11: 20 asked, 5 added, nothing shown): draw the cart
             again from the store, and open the drawer when it now holds more than before. */
          return refreshSections().then(function () {
            if (cartCount() > before && !onCartPage) drawer().open(opener);
            throw e;
          });
        });
  }

  /* The cart sections drawn again from the store, without a change (Section Rendering API). Resolves either way. */
  function refreshSections() {
    var ids = sectionIds();
    if (!ids.length) return Promise.resolve();
    /* No Accept: application/json here: on a page address that header makes the store answer with the page's own JSON, not the sections. */
    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (sections) { if (sections) render(sections); }, function () { /* keep what is shown */ });
  }
  function cartCount() {
    var el = document.querySelector('[data-cart-count]');
    return el ? parseInt(el.textContent, 10) || 0 : 0;
  }

  /* Removes one cart line (the element with data-cart-line) the same way as its remove link, including a pending quantity change.
     Used by «Flytt til ønskelisten» (assets/wishlist.js). Resolves when the cart has been rendered again. */
  function removeLine(lineEl) {
    if (!lineEl || !lineEl.isConnected) return Promise.resolve(false);
    clearTimeout(qtyTimers.get(lineEl));
    return changeLine(lineEl, 0).then(function () { return true; });
  }

  function newEngravingId() { return 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }

  /* Product + chosen engravings as one items[] payload (all or nothing). null when no engraving is chosen. */
  function engravingPayload(form) {
    var scope = form.closest('product-form') || form.closest('.shopify-section') || form.parentElement;
    var chosen = scope ? scope.querySelectorAll('[data-pippei-gravering-variant]:checked') : [];
    if (!chosen.length) return null;
    var fd = new FormData(form), props = {};
    fd.forEach(function (v, k) {
      var m = k.match(/^properties\[(.+)\]$/);
      if (m && v !== '') props[m[1]] = v;
    });
    var id = newEngravingId();
    props._gravering_id = id;
    var main = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity'), 10) || 1, properties: props };
    if (fd.get('selling_plan')) main.selling_plan = fd.get('selling_plan');
    var block = scope.querySelector('[data-pippei-gravering]');
    var title = block ? block.getAttribute('data-pippei-produkt') : '';
    var items = [main];
    chosen.forEach(function (cb) {
      var gp = { _gravering_for: id, 'Gravering til': title };
      var ready = cb.getAttribute('data-pippei-gravering-forventet');
      if (ready) gp['Forventet klar'] = ready;   // visible in checkout, like the product line
      items.push({ id: parseInt(cb.getAttribute('data-pippei-gravering-variant'), 10), quantity: 1, properties: gp });
    });
    return { items: items, chosen: chosen };
  }

  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!form.matches || !form.matches('form[data-product-form]') || !drawer()) return;
    ev.preventDefault();
    var btn = form.querySelector('[type="submit"]');
    var err = form.querySelector('[data-form-error]');
    if (err) { err.hidden = true; err.textContent = ''; }
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
    var engraving = engravingPayload(form);
    var request;
    if (engraving) {
      request = jsonPost(URL_ADD, sectionParams({ items: engraving.items }));
    } else {
      var fd = new FormData(form);
      fd.append('sections', sectionIds().join(','));
      fd.append('sections_url', window.location.pathname);
      request = fetch(URL_ADD, { method: 'POST', headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd }).then(readJson);
    }
    request
      .then(function (res) {
        if (engraving) engraving.chosen.forEach(function (cb) { cb.checked = false; });
        form.dispatchEvent(new CustomEvent('pippei:added', { bubbles: true }));
        render(res.sections);
        drawer().open(btn);
      })
      .catch(function (e) {
        if (err) { err.textContent = e.message || window.PippeiCartStrings.error; err.hidden = false; }
      })
      .then(function () { if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = false; } });
  });

  /* ---------- drawer element ---------- */

  function drawer() { return document.getElementById('CartDrawer'); }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var self = this;
      this._onKey = function (e) { self._key(e); };
      window.addEventListener('popstate', function () { if (self.classList.contains('is-open')) self.close(true); });
      updateCount();
      if (!links.reconcile()) guard.schedule();
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      this._opener = opener || document.activeElement;
      this.removeAttribute('inert');
      this.classList.add('is-open');
      lockScroll(true);
      document.addEventListener('keydown', this._onKey);
      document.querySelectorAll('[data-cart-drawer-toggle]').forEach(function (a) { a.setAttribute('aria-expanded', 'true'); });
      try { history.pushState({ pippeiCart: true }, ''); this._pushed = true; } catch (e) { this._pushed = false; }
      var panel = this.querySelector('[data-drawer-panel]');
      var self = this;
      requestAnimationFrame(function () {
        var first = self.querySelector('[data-drawer-close]:not(.cart-drawer__overlay)');
        (first || panel).focus({ preventScroll: true });
      });
    }

    close(fromHistory) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('inert', '');
      lockScroll(false);
      document.removeEventListener('keydown', this._onKey);
      document.querySelectorAll('[data-cart-drawer-toggle]').forEach(function (a) { a.setAttribute('aria-expanded', 'false'); });
      if (!fromHistory && this._pushed && history.state && history.state.pippeiCart) history.back();
      this._pushed = false;
      if (this._opener && this._opener.focus && document.contains(this._opener)) this._opener.focus({ preventScroll: true });
    }

    _key(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      var focusables = Array.prototype.filter.call(
        this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])'),
        function (el) { return el.offsetParent !== null; }
      );
      /* The wishlist notice («Flyttet til ønskelisten» with «Angre») sits outside the drawer; it is part of the Tab loop while it shows. */
      var notice = document.querySelector('[data-wl-live].is-on button');
      if (notice) focusables.push(notice);
      if (!focusables.length) return;
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  var lockedY = 0;
  function lockScroll(on) {
    var b = document.body;
    if (on) {
      lockedY = window.scrollY;
      b.style.position = 'fixed';
      b.style.top = -lockedY + 'px';
      b.style.left = '0';
      b.style.right = '0';
      b.style.width = '100%';
    } else {
      b.style.position = ''; b.style.top = ''; b.style.left = ''; b.style.right = ''; b.style.width = '';
      window.scrollTo(0, lockedY);
    }
  }

  /* ---------- «Forventet klar» guard ---------- */

  var guard = (function () {
    var PROP = 'Forventet klar';
    var ATTEMPT_LIMIT = 2, ATTEMPT_WINDOW = 60000;   // per variant and target text: max 2 calls per minute (stops flapping)
    var ROUND_LIMIT = 4, ROUND_WINDOW = 10000;        // global: max 4 rounds per 10 s; extra rounds are deferred, not dropped
    var busy = false, attempts = {}, rounds = [], retry = null, scheduled = false;

    function isLegacyProp(name) { return name !== PROP && /^_?forventet klar:?$/i.test(String(name).trim()); }

    /* An old Kalles name («_Forventet klar», «Forventet klar:») with a value: the line is rewritten so it goes. */
    function hasLegacy(props) {
      return !!props && Object.keys(props).some(function (name) {
        return isLegacyProp(name) && String(props[name]).trim() !== '';
      });
    }

    /* True when the latest dd.mm.yyyy date in the text is before today (browser date); false without a date. */
    function isPast(text) {
      var re = /\b(\d{1,2})\.(\d{1,2})\.(\d{4})\b/g, m, latest = null;
      while ((m = re.exec(text)) !== null) {
        var key = m[3] + ('0' + m[2]).slice(-2) + ('0' + m[1]).slice(-2);
        if (latest === null || key > latest) latest = key;
      }
      if (latest === null) return false;
      var d = new Date();
      var today = String(d.getFullYear()) + ('0' + (d.getMonth() + 1)).slice(-2) + ('0' + d.getDate()).slice(-2);
      return latest < today;
    }

    /* New value, or null to leave the line alone:
       - no longer waiting but has the property or an old name      -> '' (checkout hides empty values)
       - waiting, cart text has a date and differs (or an old name) -> the cart text
       - waiting without a known date, property passed or old name  -> '' (no date is better than a wrong one)
       - waiting without a known date otherwise                     -> untouched */
    function target(el, props) {
      var vent = el.getAttribute('data-vent') === 'true';
      var tekst = (el.getAttribute('data-tekst') || '').trim();
      var egenskap = (el.getAttribute('data-egenskap') || '').trim();
      var legacy = hasLegacy(props);
      if (!vent) return (egenskap !== '' || legacy) ? '' : null;
      if (tekst !== '') return (tekst !== egenskap || legacy) ? tekst : null;
      if (legacy || (egenskap !== '' && isPast(egenskap))) return '';
      return null;
    }

    function attemptsWithin(stamp, now) {
      var list = (attempts[stamp] || []).filter(function (t) { return now - t < ATTEMPT_WINDOW; });
      attempts[stamp] = list;
      return list.length;
    }

    function reconcile() {
      scheduled = false;
      if (busy) return;
      if (retry) { clearTimeout(retry); retry = null; }
      var now = Date.now();
      var ops = [], seenKey = {}, seenVariant = {};

      document.querySelectorAll('[data-pippei-fk]').forEach(function (el) {
        var key = el.getAttribute('data-key');
        var variant = el.getAttribute('data-variant') || key;
        var qty = parseInt(el.getAttribute('data-qty'), 10);
        if (!key || !(qty > 0) || seenKey[key]) return;   // the same line can be in the drawer and on the cart page
        seenKey[key] = true;
        var props = parseProps(el.getAttribute('data-props'));
        var value = target(el, props);
        if (value === null) return;
        /* One operation per variant per round: Shopify merges same-variant lines when their properties become
           equal, which makes the other line's key and quantity stale. The next round uses fresh keys. */
        if (seenVariant[variant]) return;
        /* The engraving link (stable across key changes) keeps same-variant engraving lines apart, so a third
           identical engraving is not held back by the per-stamp limit. */
        var link = props ? (props._gravering_for || props._gravering_id || '') : '';
        var stamp = variant + '|' + link + '|' + value;
        if (attemptsWithin(stamp, now) >= ATTEMPT_LIMIT) return;
        if (props === null) { console.error('cart guard: unreadable properties, line skipped', key); return; }
        Object.keys(props).forEach(function (name) { if (isLegacyProp(name)) delete props[name]; });
        props[PROP] = value;
        seenVariant[variant] = true;
        ops.push({ stamp: stamp, body: { id: key, quantity: qty, properties: props } });
      });
      if (!ops.length) return;

      rounds = rounds.filter(function (t) { return now - t < ROUND_WINDOW; });
      if (rounds.length >= ROUND_LIMIT) {
        retry = setTimeout(reconcile, ROUND_WINDOW - (now - rounds[0]) + 50);
        return;
      }
      rounds.push(now);

      busy = true;
      setBusy(true);
      var last = ops.length - 1;
      var lastSections = null;
      ops.reduce(function (chain, op, i) {
        return chain.then(function () {
          (attempts[op.stamp] = attempts[op.stamp] || []).push(Date.now());
          var body = op.body;
          if (i === last) { body.sections = sectionIds().join(','); body.sections_url = window.location.pathname; }
          return jsonPost(URL_CHANGE, body).then(function (cart) { if (i === last) lastSections = cart.sections; });
        });
      }, Promise.resolve())
        .catch(function (e) { console.error('cart guard', e); })
        .then(function () {
          busy = false;
          setBusy(false);
          if (lastSections) render(lastSections);   // render() schedules the next round, which finds nothing to do
        });
    }

    return {
      schedule: function () {
        if (scheduled) return;
        scheduled = true;
        setTimeout(reconcile, 0);
      }
    };
  })();

  /* ---------- engraving link guard ---------- */

  var links = (function () {
    var ROUND_LIMIT = 3, ROUND_WINDOW = 30000;   // stops a loop if Shopify keeps refusing a repair
    var busy = false, rounds = [];

    /* Reads the drawer (it always lists every line). Returns true when a repair was queued. */
    function reconcile() {
      if (busy) return false;
      var root = document.querySelector('#CartDrawer [data-cart-root-content]') || document;
      var lines = root.querySelectorAll('[data-cart-line]');
      var ids = {}, fors = {};
      lines.forEach(function (el) {
        var id = el.getAttribute('data-engraving-id'), f = el.getAttribute('data-engraving-for');
        if (id) ids[id] = true;
        if (f) fors[f] = true;
      });
      var orphans = [], bare = [];
      lines.forEach(function (el) {
        var id = el.getAttribute('data-engraving-id'), f = el.getAttribute('data-engraving-for');
        if (f && !ids[f]) orphans.push(el.getAttribute('data-line-key'));
        else if (id && !fors[id]) bare.push(el);
      });
      if (!orphans.length && !bare.length) return false;

      var now = Date.now();
      rounds = rounds.filter(function (t) { return now - t < ROUND_WINDOW; });
      if (rounds.length >= ROUND_LIMIT) { console.error('engraving guard: round limit reached'); return false; }
      rounds.push(now);

      /* Engravings without product: removed in one update.js call. Product without engravings: _gravering_id is
         set to '' with change.js (Shopify ignores an empty property set, and an empty value means «no link» in the
         contract). One change per variant per round, because Shopify can merge same-variant lines. */
      var ops = [], seenVariant = {};
      if (orphans.length) {
        var updates = {};
        orphans.forEach(function (k) { updates[k] = 0; });
        ops.push({ url: URL_UPDATE, body: { updates: updates } });
      }
      bare.forEach(function (el) {
        var variant = el.getAttribute('data-variant');
        var props = parseProps(el.getAttribute('data-props'));
        var qty = parseInt(el.getAttribute('data-qty'), 10);
        if (props === null || !(qty > 0) || seenVariant[variant]) return;
        seenVariant[variant] = true;
        props._gravering_id = '';
        ops.push({ url: URL_CHANGE, body: { id: el.getAttribute('data-line-key'), quantity: qty, properties: props } });
      });
      if (!ops.length) return false;

      busy = true;
      var sections = null, last = ops.length - 1;
      changeQueue = changeQueue.then(function () {
        setBusy(true);
        return ops.reduce(function (chain, op, i) {
          return chain.then(function () {
            return jsonPost(op.url, i === last ? sectionParams(op.body) : op.body)
              .then(function (cart) { if (i === last) sections = cart.sections; });
          });
        }, Promise.resolve())
          .catch(function (e) { console.error('engraving guard', e); })
          .then(function () {
            busy = false;
            setBusy(false);
            if (sections) render(sections); else guard.schedule();
          });
      });
      return true;
    }

    return { reconcile: reconcile };
  })();

  customElements.define('cart-drawer', CartDrawer);

  window.PippeiCart = { open: function () { drawer() && drawer().open(); }, guard: guard, add: addItems, remove: removeLine };
})();
